import { forwardRef, useId, useMemo } from 'react'
import { TextInput as RNTextInput, type NativeSyntheticEvent, type TextInputFocusEventData } from 'react-native'

import { FormInput, Input, InputField, type FormInputProps, type InputFieldProps, type InputProps } from '@/components'

export interface TextInputProps extends Omit<FormInputProps, 'children'> {
  accessibilityLabel?: string
  inputProps?: InputProps
  inputFieldProps?: Omit<InputFieldProps, 'onChange' | 'onChangeText' | 'value'>
  onBlur?: (event: NativeSyntheticEvent<TextInputFocusEventData>) => void
  onChange: (value: string) => void
  value?: string | null | undefined
}

export const TextInput = forwardRef<RNTextInput, TextInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      error,
      helper,
      inputProps,
      inputFieldProps,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const nativeId = useId()
    const accessibilityLabel = useMemo(
      () => _accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`,
      [_accessibilityLabel, label, isRequired, helper]
    )

    return (
      <FormInput
        error={error}
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        nativeID={nativeId}
        {...props}
      >
        <Input {...inputProps}>
          <InputField
            accessibilityLabel={accessibilityLabel}
            aria-disabled={isDisabled}
            aria-errormessage={error ? `${nativeId}-error` : undefined}
            aria-invalid={!!error}
            aria-label={accessibilityLabel} // needed because gluestack is stupid
            aria-required={isRequired}
            className='rounded bg-white font-poppinsRegular color-black'
            onBlur={onBlur}
            onChangeText={onChange}
            // @ts-ignore due to gluestack's incorrect types
            ref={ref}
            returnKeyType='done'
            submitBehavior='submit'
            value={value ?? ''}
            {...inputFieldProps}
          />
        </Input>
      </FormInput>
    )
  }
)

TextInput.displayName = 'TextInput'
